// src/pages/Checkout/CheckoutSuccess.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
  IconButton,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";

import request from "../../utilities/requests";
import formatDate from "../../utilities/formatDate";

// --- utils (placeholders; replace with your shared utils if you have them) ---
const formatMoney = (n = 0, currency = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency }).format(
    Number(n) / 100
  );

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

// --- Page Component ---
export default function CheckoutSuccess() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { eventId, paymentIntentId } = useParams();

  // Local state (to be hydrated from server)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [loadingMessage, setLoadingMessage] = useState("Retrieving your order…");

  const [receipt, setReceipt] = useState({
    paymentIntentId: paymentIntentId || null,
    currency: "USD",
    totals: { subtotalCents: 0, feesCents: 0, taxCents: 0, totalCents: 0 },
    event: { title: "", whenText: "", venue: "" },
    purchaser: { name: "", email: "" },
    items: [],
  });

  const retryTimeoutRef = useRef(null);

  const getRetryPlan = (attempt) => {
    // attempt is 0-indexed
    if (attempt < 5) {
      return { delay: 1000, message: "Retrieving your order…" };
    } else if (attempt < 8) {
      return { delay: 2000, message: "Still trying…" };
    } else if (attempt < 10) {
      return { delay: 5000, message: "This is taking longer than usual…" };
    }
    return { delay: null, message: null };
  };

  // Helper: fetch receipt (separated from useEffect)
  const fetchReceipt = async ({ signal, attempt = 0, maxAttempts = 10 }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await request(
        `ticketing/order/?paymentIntentId=${paymentIntentId}`
      );

      if (signal?.aborted) return;

      if (res?.success) {
        if (res.data) {
          const d = res.data;

          // Group per-ticket items into display lines by ticket type name
          const grouped = new Map();
          const rawItems = Array.isArray(d.items) ? d.items : [];
          for (const it of rawItems) {
            const name = it.ticketTypeName || it.name || "Ticket";
            const unit = Number(it.unitPrice ?? it.lineTotal ?? 0);
            const discount = Number(it.lineDiscount)
            const g = grouped.get(name) || {
              name,
              quantity: 0,
              priceCents: unit,
              discount
            };
            g.quantity += 1;
            if (!g.priceCents && unit) g.priceCents = unit;
            grouped.set(name, g);
          }
          const displayItems = Array.from(grouped.values());

          // Totals from new fields (fallbacks to computed subtotal when needed)
          const subtotalCents =
            Number(d.amountSubtotal ?? 0) ||
            displayItems.reduce(
              (s, x) => s + (x.priceCents || 0) * (x.quantity || 0),
              0
            );
          const feesCents = Number(d.amountFees ?? 0);
          const taxCents = Number(d.amountTax ?? 0);
          const discountCents = rawItems.reduce((sum, it) => sum + Number(it?.lineDiscount || 0), 0);
          const totalCents = Number(
            d.amountTotal ??
              subtotalCents - discountCents + taxCents + feesCents
          );

          const purchaserName = [d.firstName, d.lastName]
            .filter(Boolean)
            .join(" ");

          setReceipt({
            orderId: d.customerOrderId || receipt.orderId,
            cartId: null,
            paymentIntentId: d.paymentIntentId || receipt.paymentIntentId,
            currency: (d.currency || "USD").toUpperCase(),
            totals: { subtotalCents, discountCents, feesCents, taxCents, totalCents },
            event: {
              title: d.eventId?.name || "",
              whenText:
                formatDate(d.eventId?.start) ||
                formatDate(d.eventId?.date) ||
                "",
              venue: d.eventId?.venueName || "",
            },
            purchaser: { name: purchaserName, email: d.email || "" },
            items: displayItems,
          });
          // Success: clear any scheduled retry and stop spinner
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
          }
          setLoading(false);
        } else {
          // res.success but data is null => order not fully resolved yet; retry with backoff
          if (attempt < maxAttempts) {
            const { delay, message } = getRetryPlan(attempt);
            if (delay && !signal?.aborted) {
              setLoadingMessage(message || "Retrieving your order…");
              if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
              retryTimeoutRef.current = setTimeout(() => {
                fetchReceipt({ signal, attempt: attempt + 1, maxAttempts });
              }, delay);
              return; // keep loading true while a retry is scheduled
            }
            // No more retries configured
            if (retryTimeoutRef.current) {
              clearTimeout(retryTimeoutRef.current);
              retryTimeoutRef.current = null;
            }
            setError("We couldn’t load your receipt.");
            setLoading(false);
            return;
          } else {
            if (retryTimeoutRef.current) {
              clearTimeout(retryTimeoutRef.current);
              retryTimeoutRef.current = null;
            }
            setError("We couldn’t load your receipt.");
            setLoading(false);
            return;
          }
        }
      } else {
        if (retryTimeoutRef.current) { clearTimeout(retryTimeoutRef.current); retryTimeoutRef.current = null; }
        setError(res?.error || "We couldn’t load your receipt.");
        setLoading(false);
      }
    } catch (e) {
      if (retryTimeoutRef.current) { clearTimeout(retryTimeoutRef.current); retryTimeoutRef.current = null; }
      if (!signal?.aborted) {
        setError(e?.message || "We couldn’t load your receipt.");
        setLoading(false);
      }
    } finally {
      if (!signal?.aborted) {
        // If a retry is scheduled, remain in loading state
        const hasRetryScheduled = Boolean(retryTimeoutRef.current);
        if (!hasRetryScheduled) setLoading(false);
      }
    }
  };

  // --- Hydration: fetch the final order/receipt ---
  useEffect(() => {
    const controller = new AbortController();

    if (!paymentIntentId) {
      // If we got here without a PI, show an error and stop loading
      setLoading(false);
      setLoadingMessage("Retrieving your order…");
      setError(
        "Missing payment reference. Please check your email for a receipt or contact support."
      );
      return () => controller.abort();
    }

    fetchReceipt({ signal: controller.signal });
    return () => {
      controller.abort();
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
    // Re-run if PI changes (e.g., navigating here with state after success)
  }, [paymentIntentId]);

  const total = receipt.totals.totalCents ?? 0;
  const isFree = Number(total) === 0;

  // --- Handlers ---
  const goBackToEvent = () => navigate(`/tickets/${eventId}`);
  const goToEvents = () => navigate(`/events`);
  const viewTickets = () => {
    // TODO: Route to your ticket wallet / order details page
    // navigate(`/orders/${receipt.orderId}`);
  };

  const handleRetry = () => {
    // Clear error and any pending scheduled retry
    setError(null);
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    const controller = new AbortController();
    setLoading(true);
    setLoadingMessage("Retrieving your order…");
    fetchReceipt({ signal: controller.signal, attempt: 0, maxAttempts: 10 });
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Top bar */}
      <Box sx={{ px: 1, py: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <IconButton onClick={goBackToEvent} aria-label="Back">
            <ArrowBackIosNewIcon />
          </IconButton>
          <Stack
            alignItems="center"
            spacing={0}
            sx={{ minHeight: 40, flex: 1 }}
          >
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              Order confirmed
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {receipt.orderId
                ? `Order #${receipt.orderId}`
                : "Thank you for your purchase"}
            </Typography>
          </Stack>
          <IconButton onClick={goToEvents} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <Divider />

      {error && !receipt.orderId ? (
        // --- Full-screen error state ---
        <Box sx={{ px: 2, py: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Stack spacing={2} alignItems="center" sx={{ maxWidth: 560, textAlign: 'center' }}>
            <ReportProblemRoundedIcon color="warning" sx={{ fontSize: 48 }} />
            <Typography variant="h5">We couldn’t load your receipt</Typography>
            <Typography variant="body2" color="text.secondary">
              This sometimes happens right after payment while things finish processing. Your payment likely
              succeeded. You can try again, or check your email for the confirmation.
            </Typography>
            <Typography variant="body2" color="text.secondary">If you don't see the event in the My Events tab below in the next few minutes, please contact support {"(Profile > Settings > Contact Us)"}</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ pt: 1 }}>
              <Button variant="contained" onClick={handleRetry}>Try again</Button>
              <Button variant="outlined" onClick={() => navigate('/events')}>View events</Button>
              <Button variant="text" component={Link} to="/contactus">Contact support</Button>
            </Stack>
          </Stack>
        </Box>
      ) : (
        <>
          {/* Success badge (render only once receipt is loaded) */}
          {receipt.orderId && (
            <Box sx={{ px: 2, pt: 3,width: {sx:'100%', md: '50%'},display: 'block', mx: 'auto' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon color="success" />
                <Typography variant="h6">
                  {isFree ? "Your free order is complete" : "Payment successful"}
                </Typography>
              </Stack>
              {receipt.purchaser?.email && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  A confirmation was sent to {receipt.purchaser.email}
                </Typography>
              )}
            </Box>
          )}

          {/* Event + amount row */}
          <Box sx={{ px: 2, py: 2, width: {sx:'100%', md: '50%'},display: 'block', mx: 'auto' }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              {loading ? (
                <Box sx={{ px: 2, py: 4, display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    {loadingMessage}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="h6" sx={{ mb: 0.25 }}>
                    {receipt.event.title || "Event title"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {receipt.event.whenText || "Event date • time"}
                    {receipt.event.venue ? ` • ${receipt.event.venue}` : ""}
                  </Typography>
                </Box>
              )}

              <Typography variant="h6">
                {formatMoney(total, receipt.currency)}
              </Typography>
            </Stack>
          </Box>

          <Divider />

          {/* Line items */}
          <Box sx={{ px: 2, py: 2, width: {sx:'100%', md: '50%'},display: 'block', mx: 'auto' }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Order summary
            </Typography>
            <Stack spacing={1.25}>
              {receipt.items?.length ? (
                receipt.items.map((it, idx) => (
                  <Stack key={idx} direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">
                      {it.quantity} × {it.name}
                    </Typography>
                    <Typography variant="body2">
                      {formatMoney((it.priceCents ?? 0) * (it.quantity ?? 1), receipt.currency)}
                    </Typography>
                  </Stack>
                ))
              ) : (
                <>
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} animation="wave" height={24} />
                  ))}
                </>
              )}
              <Divider sx={{ my: 0.5 }} />
              {receipt.items?.length ? (
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">Subtotal</Typography>
                    <Typography variant="body2">
                      {formatMoney(receipt.totals.subtotalCents || 0, receipt.currency)}
                    </Typography>
                  </Stack>
                  {!!receipt.totals.discountCents && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">Discount</Typography>
                      <Typography variant="body2">
                        -{formatMoney(receipt.totals.discountCents, receipt.currency)}
                      </Typography>
                    </Stack>
                  )}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">Fees</Typography>
                    <Typography variant="body2">
                      {formatMoney(receipt.totals.feesCents || 0, receipt.currency)}
                    </Typography>
                  </Stack>
                  {!!receipt.totals.taxCents && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">Tax</Typography>
                      <Typography variant="body2">
                        {formatMoney(receipt.totals.taxCents, receipt.currency)}
                      </Typography>
                    </Stack>
                  )}
                  <Divider sx={{ my: 0.5 }} />
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Total
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {formatMoney(total, receipt.currency)}
                    </Typography>
                  </Stack>
                </>
              ) : (
                <>
                  <Skeleton animation="wave" height={24} />
                  <Skeleton animation="wave" height={24} />
                  <Skeleton animation="wave" height={24} />
                  <Divider sx={{ my: 0.5 }} />
                  <Skeleton animation="wave" height={28} />
                </>
              )}
            </Stack>
          </Box>

          {/* Actions */}
          <Box sx={{ px: 2, py: 2 }}>
            {receipt.orderId ? (
              <Stack direction="column" spacing={1}>
                <div style={{ height: 20 }} />
                <Typography align="center">You can view all of your orders in your Profile</Typography>
                <Typography align="center" style={{ height: 75 }}>
                  You should now see the event in the My Events tab
                </Typography>
                <div
                  style={{
                    position: "fixed",
                    bottom: 40,
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    marginLeft: 0,
                    display: "flex",
                    justifyContent: "center",
                    pointerEvents: "none" // avoid intercepting clicks
                  }}
                >
                  <div className="bounce-icon" style={{ pointerEvents: "auto" }}>
                    <ExpandMoreIcon
                      style={{ width: 50, height: 50, color: theme.palette.primary.main }}
                    />
                  </div>
                </div>
              </Stack>
            ) : (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Skeleton variant="rectangular" height={30} />
                <Skeleton variant="rounded" height={30} />
              </Stack>
            )}
          </Box>
        </>
      )}
      {/* Loading / error states */}
    </Box>
  );
}
